import * as preact from "preact";

import models from "models/models";
import * as languageMap from "shared/languageMap";
import LanguageSelectorView from "views/LanguageSelectorView";
import SignupView from "views/SignupView";
import * as walkthrough from "walkthrough";

interface Props {
  appRouter: any;
  options?: {
    learningLanguage?: string;
  };
}

interface State {
  step: "selectLearningLanguage" | "selectFirstLanguage" | "signup";
}

export default class StartPageView extends preact.Component<Props, State> {
  private signupView: any;

  constructor(props: Props) {
    super(props);

    const learningLanguage = props.options?.learningLanguage;

    if (learningLanguage && languageMap.map[learningLanguage]) {
      models.user
        .save(
          {
            currentLearningLanguage: learningLanguage,
          },
          { patch: true }
        )
        .then(() => {
          walkthrough.setAchievement("setLanguages", true);
          this.forceUpdate();
        });

      if (learningLanguage === "en") {
        // If learning English, ask for first language
        this.state = {
          step: "selectFirstLanguage",
        };
      } else {
        this.state = {
          step: "signup",
        };
      }
    } else if (walkthrough.hasAchievement("setLanguages")) {
      this.state = {
        step: "signup",
      };
    } else {
      this.state = {
        step: "selectLearningLanguage",
      };
    }

    this.redirectIfAppropriate();
  }

  async redirectIfAppropriate() {
    // Check if user has already signed in
    if (!(await models.user.isLocalMode())) {
      // If signed in, redirect to destination
      this.redirectToDestination();
      return;
    }
  }

  componentWillUnmount() {
    // Clean up signup view if it exists
    if (this.signupView) {
      this.signupView.remove();
    }
  }

  handleLearningLanguageSelected = async (languageCode: string) => {
    await models.user.save(
      {
        currentLearningLanguage: languageCode,
      },
      { patch: true }
    );

    if (languageCode === "en") {
      // If learning English, ask for first language
      this.setState({ step: "selectFirstLanguage" });
    } else {
      // Otherwise assume first language is English
      this.setState({
        step: "signup",
      });
      walkthrough.setAchievement("setLanguages", true);
    }
  };

  handleFirstLanguageSelected = async (languageCode: string) => {
    this.setState({
      step: "signup",
    });

    await models.user.save(
      {
        firstLanguage: languageCode,
      },
      { patch: true }
    );
    walkthrough.setAchievement("setLanguages", true);
  };

  handleSignupCancel = () => {
    localStorage.setItem("shownSignup", "true");
    this.redirectToDestination();
  };

  redirectToDestination = () => {
    const urlParams = new URLSearchParams(window.location.search);
    let redirectPath = urlParams.get("redirectPath");

    // Replace language-specific library path with generic /library path
    if (/\/[a-z]{2,3}\/library/.test(redirectPath)) {
      redirectPath = "/library";
    }
    this.props.appRouter.navigate(redirectPath ?? "/", { trigger: true });
  };

  renderLanguageDialog = (
    title: string,
    languageType: "learning" | "first",
    onLanguageSelected: (code: string) => void
  ) => {
    const flagLanguages = languageMap.flagList.slice(0, 12).map((language) => {
      const languageCode =
        typeof language === "string" ? language : language.code;
      const languageData =
        typeof language === "string" ? languageMap.map[language] : language;
      return {
        ...languageData,
        code: languageCode,
      };
    });

    return (
      <div class="startPageDialog" id="languageSelectDialog">
        <h2 style="text-align:center">{title}</h2>

        <ul class="flags">
          {flagLanguages.map((language) => (
            <li class="hasTooltip" key={language.code}>
              <div
                class="clickable"
                onClick={() => onLanguageSelected(language.code)}
              >
                <img src={`/external/flags/${language.code}.svg`} />
                <div class="language-label">{language.english}</div>
              </div>
            </li>
          ))}
        </ul>

        <div id="languageSelectorView">
          <LanguageSelectorView
            languageType={languageType}
            searchOnlyMode={true}
            setLanguage={onLanguageSelected}
          />
        </div>
      </div>
    );
  };

  renderSignupView = () => {
    return (
      <div class="startPageDialog startPageSignupDialog">
        <h2>Let's get started</h2>
        <button class="closeButton" onClick={this.handleSignupCancel}>
          <i class="icon-remove" />
        </button>
        <div
          class="signupContainer"
          ref={(el) => {
            if (el && !this.signupView) {
              // Create and render the Backbone SignupView
              this.signupView = new SignupView({
                el,
                models,
              });
              // SignupView automatically renders itself in the initialize method
            }
          }}
        />
      </div>
    );
  };

  render() {
    return (
      <>
        {this.state.step === "selectLearningLanguage" &&
          this.renderLanguageDialog(
            "What language do you want to learn?",
            "learning",
            this.handleLearningLanguageSelected
          )}

        {this.state.step === "selectFirstLanguage" &&
          this.renderLanguageDialog(
            "What is your first language?",
            "first",
            this.handleFirstLanguageSelected
          )}

        {this.state.step === "signup" && this.renderSignupView()}
      </>
    );
  }
}
